import { useGetReleasesQuery } from "@dashboard/downloads/api";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { useQueryErrorToast } from "@shared/toasts/useQueryErrorToast";

export type ReleaseFilesState = {
    data: API.ReleaseFile[];
    count: number;
    status: "pending" | "fulfilled" | "rejected" | "uninitialized";
};

const defaultState: ReleaseFilesState = {
    data: [],
    count: 0,
    status: "uninitialized",
};

export const releaseFilesPageSize = 200;

interface UseReleaseFilesOptions {
    pageSize?: number;
}

export const useReleaseFiles = (options: UseReleaseFilesOptions = {}): ReleaseFilesState => {
    const { pageSize = releaseFilesPageSize } = options;

    const { data: workspace } = useCurrentWorkspace();
    const isTerminated = workspace?.subscription?.status === "Terminated";
    const skip = !workspace?.id || isTerminated;

    const releases = useGetReleasesQuery(
        {
            workspaceId: workspace?.id ?? 0,
            pageSize,
        },
        { skip },
    );
    const { data, isUninitialized, isLoading, isFetching, isError, isSuccess } = releases;
    // Neither the downloads page nor the enrollment wizard renders an error state for a failed list.
    useQueryErrorToast(releases);

    if (!workspace?.id) {
        return defaultState;
    }

    if (isTerminated) {
        return { data: [], count: 0, status: "fulfilled" };
    }

    if (isUninitialized) {
        return defaultState;
    }

    if (isLoading || isFetching) {
        return { data: data?.list ?? [], count: data?.count ?? 0, status: "pending" };
    }

    if (isError) {
        return { data: [], count: 0, status: "rejected" };
    }

    if (isSuccess && data) {
        return { data: data.list, count: data.count, status: "fulfilled" };
    }

    return defaultState;
};
